// src/utils/useApplePayHandler.ts

import { useEffect, useState, useCallback } from 'react';
import useApplePay from './useApplePay';
import searchToObject from './searchToObject';
import { useRootContext } from '../providers/RootContext';
import { tracker } from 'ouisys-engine/utilities/tracker';

const urlParams = searchToObject();
const d_country = urlParams['d_country'] || window.pac_analytics?.visitor?.ip_range_name || 'ES';

const paymentRequest = {
  countryCode: d_country.toUpperCase(),
  currencyCode: 'EUR',
  supportedNetworks: ['visa', 'masterCard', 'discover', 'maestro', 'electron', 'girocard', 'amex'],
  merchantCapabilities: ['supports3DS', 'supportsDebit', 'supportsCredit'],
  requiredShippingContactFields: ['email'],

  total: {
    label: 'for XR Academy',
    amount: 0.01
  }
};

const useApplePayHandler = ({ setIsApplePayAvailable, isApplePayAvailable }) => {
  const { isLoading, isSafari } = useRootContext();

  const [userPaymentStatus, setUserPaymentStatus] = useState<boolean>(false);
  const { initApplePay } = useApplePay();

  useEffect(() => {
    checkApplePayAvailability();
  }, []);

  useEffect(() => {
    if (isApplePayAvailable) {
      canUserMakePayment('merchant.com.xracademy.online.2');
    }
  }, [isApplePayAvailable]);

  const checkApplePayAvailability = () => {
    if (userPaymentStatus) return;
    if (window.ApplePaySession) {
      tracker.customEvent(
        'apple-pay-flow',
        'applepay-availability-success',
        'Apple Pay session is available for the user on page load'
      );
      setIsApplePayAvailable(true);
    } else {
      tracker.customEvent(
        'apple-pay-flow',
        'applepay-availability-failed',
        'Apple Pay session is NOT available for the user on page load'
      );
      loadApplePayScript();
    }
  };

  const canUserMakePayment = (merchantId) => {
    if (window.location.protocol !== 'https:') return;
    // // Safari & IOS 18+ + Third party browsers
    // if (window.ApplePaySession && ApplePaySession.applePayCapabilities) {
    //   ApplePaySession.applePayCapabilities(merchantId)
    //     .then((capabilities) => {
    //       switch (capabilities.paymentCredentialStatus) {
    //         case 'paymentCredentialsAvailable':
    //           tracker.customEvent(
    //             'apple-pay-flow',
    //             'applepay-active-card-success',
    //             'Device supports Apple Pay - User has an active Apple Pay card on their wallet qualifies for payment.'
    //           );
    //           break;
    //         case 'paymentCredentialStatusUnknown':
    //           tracker.customEvent(
    //             'apple-pay-flow',
    //             'applepay-active-card-unknown',
    //             'Device supports Apple Pay - Wallet information unknown'
    //           );
    //           break;
    //         case 'paymentCredentialsUnavailable':
    //           tracker.customEvent(
    //             'apple-pay-flow',
    //             'applepay-active-card-failed',
    //             'Device supports Apple Pay - User DOES NOT have any active card on wallet.'
    //           );
    //           break;
    //         case 'applePayUnsupported':
    //           tracker.customEvent('apple-pay-flow', 'applepay-unsupported', 'Device DOES NOT support Apple Pay');
    //           break;
    //       }
    //     })
    //     .catch((error) => {
    //       tracker.customEvent(
    //         'apple-pay-flow',
    //         'applepay-capabilities-catch',
    //         'Error checking Apple Pay capabilities. It requires IOS18+:',
    //         {
    //           error: error?.toString()
    //         }
    //       );
    //     });
    // }
    // Safari 18- only
    if (window.ApplePaySession && ApplePaySession.canMakePaymentsWithActiveCard) {
      ApplePaySession.canMakePaymentsWithActiveCard(merchantId)
        .then((activeCard) => {
          if (ApplePaySession.canMakePayments()) {
            if (activeCard) {
              if (isSafari()) {
                tracker.customEvent(
                  'apple-pay-flow',
                  'applepay-active-card-success',
                  'Device supports Apple Pay - User has an active Apple Pay card on their wallet qualifies for payment.'
                );
              } else {
                tracker.customEvent(
                  'apple-pay-flow',
                  'applepay-active-card-unknown',
                  'Device supports Apple Pay - Wallet information unknown'
                );
              }
            } else {
              tracker.customEvent(
                'apple-pay-flow',
                'applepay-active-card-failed',
                'Device supports Apple Pay - User DOES NOT have any active card on wallet.'
              );
            }
          } else {
            tracker.customEvent('apple-pay-flow', 'applepay-unsupported', 'Device DOES NOT support Apple Pay');
          }
        })
        .catch((error) => {
          tracker.customEvent(
            'apple-pay-flow',
            'applepay-capabilities-catch',
            'Error checking Apple Pay wallet. IOS17-:',
            {
              error: error?.toString()
            }
          );
        });
    }
  };

  const handleApplePayClick = (trigger) => {
    if (isLoading) return;
    initApplePay(paymentRequest, trigger);
  };

  const loadApplePayScript = () => {
    const script = document.createElement('script');
    script.src = 'https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js';
    script.async = true;
    document.body.appendChild(script);
    script.onload = () => {
      tracker.customEvent(
        'apple-pay-flow',
        'applepay-script-loaded',
        "Apple Pay script is fully loaded for the user who didn't have Apple Pay session on page load"
      );
      if (window.ApplePaySession) {
        tracker.customEvent(
          'apple-pay-flow',
          'applepay-script-available-through-script',
          'Apple Pay script is fully loaded and Apple Pay session is active -Apple Pay Scan Camera-'
        );
        setIsApplePayAvailable(true);
      } else {
        tracker.customEvent(
          'apple-pay-flow',
          'applepay-script-loaded-but-applepay-not-active',
          'Apple Pay script is fully loaded and Apple Pay session is NOT active'
        );
      }
    };
  };

  const checkPaymentStatus = () => {
    const status = urlParams['payment-status'];
    if (status !== undefined) {
      const isSuccess = status === 'true';
      setUserPaymentStatus(isSuccess);
    }
  };

  useEffect(() => {
    checkPaymentStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    handleApplePayClick,
    userPaymentStatus,
    paymentRequest
  };
};

export default useApplePayHandler;
